// UpdateIntegrationModal.tsx

import React, { useEffect } from 'react';
import {
  SftpService,
  CustomService,
} from '@/api';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

// Define separate schemas
const updateSftpFormSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters.'),
  sftpHost: z.string().min(2, 'SFTP host must be at least 2 characters.'),
  sftpUsername: z.string().min(2, 'SFTP username must be at least 2 characters.'),
  sftpPassword: z.string().optional(),
  sftpPrivateKey: z.string().optional(),
  sftpPublicKey: z.string().optional(),
});

const updateCustomFormSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters.'),
  customHost: z.string().min(2, 'Custom host must be at least 2 characters.'),
  customUsername: z.string().min(2, 'Custom username must be at least 2 characters.'),
  customPassword: z.string().optional(),
});

interface EditingIntegration {
  id: string;
  integrationType: 'SFTP' | 'Custom';
  name: string;
  sftpHost?: string;
  sftpUsername?: string;
  sftpPassword?: string;
  sftpPrivateKey?: string;
  sftpPublicKey?: string;
  customHost?: string;
  customUsername?: string;
  customPassword?: string;
}

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  orgId: string;
  editingIntegration: EditingIntegration;
}

export function UpdateIntegrationModal({
  open,
  setOpen,
  orgId,
  editingIntegration,
}: Props) {
  const queryClient = useQueryClient();

  // Initialize forms
  const updateSftpForm = useForm<z.infer<typeof updateSftpFormSchema>>({
    resolver: zodResolver(updateSftpFormSchema),
    defaultValues: {
      name: '',
      sftpHost: '',
      sftpUsername: '',
      sftpPassword: '',
      sftpPrivateKey: '',
      sftpPublicKey: '',
    },
  });

  const updateCustomForm = useForm<z.infer<typeof updateCustomFormSchema>>({
    resolver: zodResolver(updateCustomFormSchema),
    defaultValues: {
      name: '',
      customHost: '',
      customUsername: '',
      customPassword: '',
    },
  });

  // Set default values
  useEffect(() => {
    if (editingIntegration) {
      if (editingIntegration.integrationType === 'SFTP') {
        updateSftpForm.reset({
          name: editingIntegration.name || '',
          sftpHost: editingIntegration.sftpHost || '',
          sftpUsername: editingIntegration.sftpUsername || '',
          sftpPassword: editingIntegration.sftpPassword || '',
          sftpPrivateKey: editingIntegration.sftpPrivateKey || '',
          sftpPublicKey: editingIntegration.sftpPublicKey || '',
        });
      } else if (editingIntegration.integrationType === 'Custom') {
        updateCustomForm.reset({
          name: editingIntegration.name || '',
          customHost: editingIntegration.customHost || '',
          customUsername: editingIntegration.customUsername || '',
          customPassword: editingIntegration.customPassword || '',
        });
      }
    }
  }, [editingIntegration]);

  // Mutation
  const updateIntegrationMutation = useMutation<
    any,
    Error,
    { payload: any; integrationType: string }
  >({
    mutationFn: async ({ payload, integrationType }) => {
      if (integrationType === 'SFTP') {
        return await SftpService.updateSftp(payload);
      } else if (integrationType === 'Custom') {
        return await CustomService.updateCustom(payload);
      }
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['getIntegrations', orgId] });
      setOpen(false);
      if (variables.integrationType === 'SFTP') {
        updateSftpForm.reset();
      } else if (variables.integrationType === 'Custom') {
        updateCustomForm.reset();
      }
      toast.success(
        `${variables.integrationType} Integration updated successfully!`,
      );
    },
    onError: (error) => {
      toast.error('An error occurred while updating the integration.', {
        description: error.message,
      });
    },
  });

  // OnSubmit functions
  const onSubmitSftp = (values: z.infer<typeof updateSftpFormSchema>) => {
    console.log('Submitting SFTP form with values:', values);
    const payload = {
      orgId,
      id: editingIntegration.id,
      ...values,
    };
    updateIntegrationMutation.mutate({
      payload,
      integrationType: 'SFTP',
    });
  };

  const onSubmitCustom = (values: z.infer<typeof updateCustomFormSchema>) => {
    console.log('Submitting Custom form with values:', values);
    const payload = {
      orgId,
      id: editingIntegration.id,
      ...values,
    };
    updateIntegrationMutation.mutate({
      payload,
      integrationType: 'Custom',
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Update {editingIntegration.integrationType} Integration
          </DialogTitle>
          <DialogDescription>
            Modify the details of your existing {editingIntegration.integrationType} integration.
          </DialogDescription>
        </DialogHeader>

        {editingIntegration.integrationType === 'SFTP' && (
          <Form {...updateSftpForm}>
            <form
              onSubmit={updateSftpForm.handleSubmit(onSubmitSftp)}
              className="space-y-4"
            >
              {/* Name Field */}
              <FormField
                control={updateSftpForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter Integration Name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* SFTP Host Field */}
              <FormField
                control={updateSftpForm.control}
                name="sftpHost"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>SFTP Host</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter SFTP Host" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* SFTP Username Field */}
              <FormField
                control={updateSftpForm.control}
                name="sftpUsername"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>SFTP Username</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter SFTP Username" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* SFTP Password Field */}
              <FormField
                control={updateSftpForm.control}
                name="sftpPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>SFTP Password</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="password"
                        placeholder="Enter SFTP Password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* SFTP Private Key Field */}
              <FormField
                control={updateSftpForm.control}
                name="sftpPrivateKey"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>SFTP Private Key</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter SFTP Private Key" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* SFTP Public Key Field */}
              <FormField
                control={updateSftpForm.control}
                name="sftpPublicKey"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>SFTP Public Key</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter SFTP Public Key" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end space-x-4">
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button
                  type="submit"
                  disabled={updateIntegrationMutation.isPending}
                >
                  {updateIntegrationMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    'Update'
                  )}
                </Button>
              </div>
            </form>
          </Form>
        )}

        {editingIntegration.integrationType === 'Custom' && (
          <Form {...updateCustomForm}>
            <form
              onSubmit={updateCustomForm.handleSubmit(onSubmitCustom)}
              className="space-y-4"
            >
              {/* Name Field */}
              <FormField
                control={updateCustomForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter Integration Name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Custom Host Field */}
              <FormField
                control={updateCustomForm.control}
                name="customHost"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Custom Host</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter Custom Host" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Custom Username Field */}
              <FormField
                control={updateCustomForm.control}
                name="customUsername"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Custom Username</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter Custom Username" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Custom Password Field */}
              <FormField
                control={updateCustomForm.control}
                name="customPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Custom Password</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="password"
                        placeholder="Enter Custom Password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end space-x-4">
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button
                  type="submit"
                  disabled={updateIntegrationMutation.isPending}
                >
                  {updateIntegrationMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    'Update'
                  )}
                </Button>
              </div>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
